/**
 * @fileoverview The register's tenders (`payments`) — the states a tender row can hold. Declared once.
 *
 * ## Why it lives here
 *
 * The `payments` table is pos-api's: a register sale's tenders. The vocabulary was declared only in pos-api's
 * `constants/statuses.js` as `PAYMENT_STATUS`. REGISTER-WEB-ORDERS-001 B7b (GAMING 1341Z) reads whether a register
 * tender still holds a customer's money from purchase-api as well (`@puffin-core/order-engine` `registerMoney`), so the
 * vocabulary MOVED here with every pos-api reader, RENAMED on the move (GAMING 1352Z, ruling (a)): beside
 * `ORDER_PAYMENT_STATUS` (an order's payment) a shared `PAYMENT_STATUS` would not say whose payment it is. pos-api no
 * longer declares it; pos-web's own mirror is held equal to this by the `uiApiEnumParity` contract.
 *
 * @module @puffin-core/status-taxonomy/registerPayment
 */

'use strict';

/**
 * The ONE register-tender status vocabulary: the states a `payments` row can hold (FULL-MERGE-1001 Q-FM-8
 * follow-up). Its writers are pos-api's `Transaction.create` (a sale's tenders, through
 * `TRANSACTION_TO_PAYMENT_STATUS`), the refund ledger (`refundLedgerWrites`) and `Payment.create` /
 * `Payment.updateStatus`, which validate against {@link REGISTER_PAYMENT_STATUSES}. It used to also carry
 * `completed` — the transaction HEADER's word, which no tender is ever written as — and the signup
 * step's two card-stored outcomes, which belong to a signup SESSION (pos-api `SIGNUP_PAYMENT_STATUS`);
 * so `GET /v1/financial/payments ?status` accepted, and pos-web's ledger offered, filters no row
 * could match.
 *
 * @type {Readonly<Object<string, string>>}
 */
const REGISTER_PAYMENT_STATUS = Object.freeze({
  PENDING: 'pending',
  AUTHORIZED: 'authorized',
  CAPTURED: 'captured',
  FAILED: 'failed',
  REFUNDED: 'refunded',
});

/**
 * `Object.values(REGISTER_PAYMENT_STATUS)` — what every model enum and route validator builds from.
 *
 * @type {ReadonlyArray<string>}
 */
const REGISTER_PAYMENT_STATUSES = Object.freeze(Object.values(REGISTER_PAYMENT_STATUS));

module.exports = { REGISTER_PAYMENT_STATUS, REGISTER_PAYMENT_STATUSES };
